import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import FornecedorDashboard from './home/FornecedorDashboard';
import NovaAnaliseSheet from './home/NovaAnaliseSheet';
import AnaliseDashboard from './home/AnaliseDashboard';
import RevisorHome from './home/RevisorHome';
import { normalizeReport, periodLabel } from './home/revisorData';

const API_URL = process.env.REACT_APP_API || 'https://api.vidal-app.com';

const getAuthHeaders = () => {
  try {
    const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
    const projeto = localStorage.getItem('vidal_selected_project');
    const headers = {};
    if (authData.token) headers['Authorization'] = `Bearer ${authData.token}`;
    if (projeto) headers['projeto'] = projeto;
    return headers;
  } catch {
    return {};
  }
};

// Month names mapping
const monthNames = {
  '01': 'JANEIRO',
  '02': 'FEVEREIRO',
  '03': 'MARÇO',
  '04': 'ABRIL',
  '05': 'MAIO',
  '06': 'JUNHO',
  '07': 'JULHO',
  '08': 'AGOSTO',
  '09': 'SETEMBRO',
  '10': 'OUTUBRO',
  '11': 'NOVEMBRO',
  '12': 'DEZEMBRO'
};

export default function RevisorDashboard() {
  // Função para obter primeiro dia do mês
  const getFirstDayOfMonth = (date = new Date()) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const day = String(firstDay.getDate()).padStart(2, '0');
    const monthStr = String(firstDay.getMonth() + 1).padStart(2, '0');
    return `${day}/${monthStr}/${firstDay.getFullYear()}`;
  };

  // Função para obter último dia do mês
  const getLastDayOfMonth = (date = new Date()) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const lastDay = new Date(year, month + 1, 0);
    const day = String(lastDay.getDate()).padStart(2, '0');
    const monthStr = String(lastDay.getMonth() + 1).padStart(2, '0');
    return `${day}/${monthStr}/${lastDay.getFullYear()}`;
  };

  // Função para obter data de hoje
  const getToday = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${today.getFullYear()}`;
  };

  const [reports, setReports] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [targetMonth, setTargetMonth] = useState(null);
  const [pendingOpenId, setPendingOpenId] = useState(null);
  const knownAtStart = React.useRef(new Set());

  // Meses que já têm análise, para avisar na criação
  const analyzedMonths = React.useMemo(() => new Set(reports.map((r) => normalizeReport(r).startKey).filter(Boolean)), [reports]);

  // "Ver análise" ao concluir: abre o relatório quando ele chegar na lista.
  // O websocketTaskService não guarda o reportId; sem ele, abre a análise que
  // não existia quando o processamento começou (a mais recente entre as novas).
  useEffect(() => {
    if (!pendingOpenId) return;
    let found = null;
    if (pendingOpenId.id) found = reports.find((r) => r.id === pendingOpenId.id);
    else {
      const fresh = reports.filter((r) => !pendingOpenId.known.has(r.id));
      found = fresh.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))[0];
    }
    if (found) { setSelectedReport(found); setPendingOpenId(null); }
  }, [reports, pendingOpenId]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [projectId, setProjectId] = useState(null); // ID do projeto criado no backend
  
  // Upload States
  const [uploadTab, setUploadTab] = useState('files'); // 'files' | 'logs'
  const [uploadLogs, setUploadLogs] = useState([]); // { type: 'success'|'error', message: string, timestamp: string }
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [stats, setStats] = useState({
    totalReports: 0,
    totalXMLs: 0,
    faltantes: 0,
    encontradas: 0,
    totalCompanies: 0
  });

  const [startDate, setStartDate] = useState(getFirstDayOfMonth()); // Primeiro dia do mês atual
  const [endDate, setEndDate] = useState(getToday()); // Data de hoje
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  // Upload step state
  const [currentStep, setCurrentStep] = useState('config'); // 'config' | 'upload' | 'processing'
  const [requiredSpeds, setRequiredSpeds] = useState([]);
  const [uploadedCount, setUploadedCount] = useState(0);
  
  // Modal state
  const [showNovoRelatorioModal, setShowNovoRelatorioModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  
  // Get current project from localStorage
  const getCurrentProject = () => {
    try {
      const projectId = localStorage.getItem('vidal_selected_project');
      if (!projectId) {
        console.error('[Revisor] No project selected');
        return null;
      }
      return { id: projectId };
    } catch (error) {
      console.error('[Revisor] Error getting currentProject:', error);
      return null;
    }
  };

  useEffect(() => {
    fetchReports();
    fetchCompanies();
    fetchKPIs();
    
    // Initialize websocketTaskService for task tracking
    const websocketTaskService = require('../../services/websocketTaskService').default;
    websocketTaskService.connect();
    
    // Check if there's already a running task
    const existingTasks = websocketTaskService.getTasks();
    const runningRevisorTask = existingTasks.find(t => 
      t.type === 'process_speds_revisor' && 
      (!t.status || t.status === 'running')
    );
    
    if (runningRevisorTask) {
      console.log('[Revisor] Found running task on mount:', runningRevisorTask);
      setProcessing(true);
      setCurrentStep('processing');
      if (runningRevisorTask.tasks && Array.isArray(runningRevisorTask.tasks)) {
        setProcessingTasks(runningRevisorTask.tasks);
      } else if (runningRevisorTask.message || runningRevisorTask.progress !== undefined) {
        setProcessingTasks([{
          id: 1,
          name: runningRevisorTask.message || 'Processando SPEDs...',
          progress: runningRevisorTask.progress || 0,
          status: 'running'
        }]);
      }
    }
    
    // Subscribe to task updates
    const unsubscribe = websocketTaskService.subscribe((tasks) => {
      // Find our revisor task
      const revisorTask = tasks.find(t => 
        t.type === 'process_speds_revisor' && 
        (t.status === 'running' || t.status === 'completed' || t.status === 'failed')
      );
      
      if (revisorTask) {
        console.log('[Revisor] Task update from websocketTaskService:', revisorTask);
        
        // Update processing tasks if we have detailed task info
        if (revisorTask.tasks && Array.isArray(revisorTask.tasks)) {
          setProcessingTasks(revisorTask.tasks);
        }
        
        // Handle completion
        if (revisorTask.status === 'completed') {
          console.log('[Revisor] Processing completed!');
          setProcessing(false);
          setCurrentStep('upload');
          setTimeout(() => {
            fetchReports();
          }, 2000);
        }
        
        // Handle failure
        if (revisorTask.status === 'failed') {
          console.error('[Revisor] Processing failed:', revisorTask.message);
          setProcessing(false);
          setCurrentStep('upload');
        }
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, []);

  // Polling de segurança enquanto processa
  useEffect(() => {
    let interval;
    if (processing) {
       interval = setInterval(() => {
           fetchReports();
       }, 5000);
    }
    return () => clearInterval(interval);
  }, [processing]);

  const fetchKPIs = async () => {
    try {
      const projectId = localStorage.getItem('vidal_selected_project');
      const query = projectId ? `?projectId=${projectId}` : '';
      const response = await fetch(`${API_URL}/revisor/kpi${query}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();

      if (data) {
        setStats({
            totalReports: data.totalReports || 0,
            totalCompanies: data.totalCompanies || 0,
            encontradas: data.xmlsValidated || 0,
            faltantes: data.pendingDocs || 0
        });
      }
    } catch (error) {
      console.error('[Revisor] Error fetching KPIs:', error);
    }
  };

  // Drag and Drop Logic
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.relatedTarget === null) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Iterar sobre todos os arquivos arrastados
      Array.from(e.dataTransfer.files).forEach(file => {
        const reader = new FileReader();

        reader.onload = (event) => {
          const content = event.target.result;
          const lines = content.split('\n');
          const reg0000 = lines.find(line => line.startsWith('|0000|'));
          
          if (reg0000) {
              const parts = reg0000.split('|');
              const dtIniRaw = parts[4];
              const dtFinRaw = parts[5];
              const cnpjRaw = parts[7];
              
              if (dtIniRaw && dtFinRaw && cnpjRaw) {
                  const formatDate = (d) => `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4, 8)}`;
                  const newStartDate = formatDate(dtIniRaw);
                  const newEndDate = formatDate(dtFinRaw);
                  
                  // Atualiza datas apenas se for o primeiro arquivo ou expande o range? 
                  // Por simplicidade, mantemos o comportamento de atualizar a UI com o último arquivo processado
                  // ou idealmente devemos verificar se os arquivos são congruentes.
                  // Para este fix rápido, vamos deixar atualizar, mas o usuário deve arrastar arquivos do mesmo período/projeto preferencialmente.
                  
                  setStartDate(newStartDate);
                  setEndDate(newEndDate);
                  
                  // Handling Company Selection with robust check
                  const foundCompany = availableCompanies.find(c => {
                      if (!c.cnpj) return false;
                      const compCnpj = String(c.cnpj).replace(/\D/g, '');
                      const fileCnpj = String(cnpjRaw).replace(/\D/g, '');
                      return compCnpj === fileCnpj;
                  });

                  if (foundCompany) {
                      // Adicionar à seleção sem remover as anteriores se já estiver selecionada?
                      // O código original setava SelectedCompanies([foundCompany.id]).
                      // Se arrastar multiplos, queremos selecionar todos eles.
                      
                      setSelectedCompanies(prev => {
                          if (!prev.includes(foundCompany.id)) {
                              return [...prev, foundCompany.id];
                          }
                          return prev;
                      });

                      toast.success(`Arquivo reconhecido: ${file.name} (${foundCompany.name || foundCompany.label})`);
                      
                      // AUTO-ADVANCE LOGIC
                      // Recalcula requeridos baseado no novo range e empresas selecionadas
                      // Precisamos fazer isso com cuidado pois setState é async.
                      // Vamos confiar que o usuário ajustou ou que o fluxo manual lidará com isso.
                      // Mas para upload automático:
                      
                      // 1. Calculate requirements explicitly (bypassing state delay)
                      // Nota: Usamos 'foundCompany' atual + o que já estava selecionado? 
                      // Para garantir consistência no loop, calculamos apenas para este arquivo.
                      
                      const companies = [foundCompany];
                      const required = calculateRequiredSpeds(newStartDate, newEndDate, companies);
                      
                      // Atualizar requiredSpeds mesclando com o que já tem
                      setRequiredSpeds(prev => {
                          // Merge avoid duplicates
                          const newReqs = required.filter(r => !prev.some(p => p.id === r.id));
                          return [...prev, ...newReqs];
                      });
                      
                      setCurrentStep('upload');

                      // 2. Tentar upload imediato
                      // Precisamos saber se este arquivo específico corresponde a uma exigência.
                      
                      const fileMonth = parseInt(dtIniRaw.substring(2, 4));
                      const fileYear = parseInt(dtIniRaw.substring(4, 8));
                      
                      // Criar objeto de requerimento "on the fly" para este arquivo para permitir upload
                      // mesmo que o state React ainda não tenha atualizado requiredSpeds
                      const matchRequirement = {
                          id: `${foundCompany.id}_${fileYear}${String(fileMonth).padStart(2,'0')}`,
                          companyId: foundCompany.id,
                          companyName: foundCompany.name,
                          companyCnpj: foundCompany.cnpj,
                          year: fileYear,
                          month: fileMonth,
                          startDate: dtIniRaw, // DDMMAAAA
                          endDate: dtFinRaw    // DDMMAAAA
                      };
                      
                      // Upload the file
                      internalUploadSpedFile(matchRequirement, file).then(success => {
                          if (success) {
                              toast.info(`Arquivo ${file.name} carregado.`);
                          }
                      });

                  } else {
                      toast.warning(`Empresa do arquivo ${file.name} (CNPJ ${cnpjRaw}) não encontrada na lista.`);
                  }
              } else {
                  toast.error(`Não foi possível identificar dados no arquivo ${file.name}.`);
              }
          } else {
              toast.error(`Arquivo inválido: ${file.name}`);
          }
        };
        
        const blob = file.slice(0, 2048);
        reader.readAsText(blob);
      });
    }
  };

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const projectId = localStorage.getItem('vidal_selected_project');
      if (!projectId) {
        console.warn('No project selected');
        setLoadingCompanies(false);
        return;
      }

      const response = await fetch(`${API_URL}/certificates/list/${projectId}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();

      if (data.success && data.data) {
        console.log('Companies data:', data.data);
        // Transformar certificados em lista de empresas únicas
        const companiesMap = new Map();
        data.data.forEach(cert => {
          if (!companiesMap.has(cert.cnpj)) {
            companiesMap.set(cert.cnpj, {
              id: cert._id,
              name: cert.companyName,
              cnpj: cert.cnpj,
              color: cert.color,
              city: cert.city,
              state: cert.state,
              neighborhood: cert.neighborhood
            });
          }
        });
        const companies = Array.from(companiesMap.values());
        console.log('Processed companies:', companies);
        setAvailableCompanies(companies);
        // Selecionar todas as empresas do projeto
        setSelectedCompanies(companies.map(c => c.id));
        console.log('Auto-selected all companies:', companies.length);
      } else {
        console.error('Failed to fetch companies:', data.message);
        setAvailableCompanies([]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setAvailableCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const [processingTasks, setProcessingTasks] = useState([]);
  
  const fetchReports = async () => {
    setLoading(true);
    try {
      const projectId = localStorage.getItem('vidal_selected_project');
      const query = projectId ? `?projectId=${projectId}` : '';
      
      const response = await fetch(`${API_URL}/revisor/reports${query}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();

      if (data.success && Array.isArray(data.data)) {
        setReports(data.data);
        setUpdatedAt(new Date());
      } else {
        setReports([]);
      }
    } catch (error) {
      console.error('Erro ao buscar relatórios:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = (reportId, reportName) => {
    setDeleteConfirm({ reportId, reportName });
  };

  const confirmDeleteReport = async () => {
    if (!deleteConfirm) return;
    
    const { reportId } = deleteConfirm;
    setDeleteConfirm(null);
    setDeletingId(reportId);
    
    // Esperar animação terminar antes de remover
    await new Promise(resolve => setTimeout(resolve, 400));
    
    try {
      setReports(prev => prev.filter(r => r.id !== reportId));
      setDeletingId(null);
      
      const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
      const token = authData.token;
      const projectId = localStorage.getItem('vidal_selected_project');
      
      const response = await fetch(`${API_URL}/revisor/reports/${reportId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'projeto': projectId
        }
      });
      
      if (!response.ok) throw new Error('Erro ao deletar');
      
      toast.success('Relatório deletado com sucesso');
      fetchKPIs();
    } catch (error) {
      console.error('Erro ao deletar relatório:', error);
      toast.error('Erro ao deletar relatório');
      fetchReports();
    }
  };

  // Calculate required SPEDs based on date range and selected companies
  const calculateRequiredSpeds = (start, end, companies) => {
    const required = [];
    const startParsed = parseDate(start);
    const endParsed = parseDate(end);
    
    // For each company
    companies.forEach(company => {
      // For each month in range
      let current = new Date(startParsed.getFullYear(), startParsed.getMonth(), 1);
      const endMonth = new Date(endParsed.getFullYear(), endParsed.getMonth(), 1);
      
      while (current <= endMonth) {
        const monthStr = String(current.getMonth() + 1).padStart(2, '0');
        required.push({
          id: `${company.id}_${current.getFullYear()}${monthStr}`,
          companyId: company.id,
          companyName: company.name,
          companyCnpj: company.cnpj,
          companyColor: company.color,
          year: current.getFullYear(),
          month: current.getMonth() + 1,
          monthName: monthNames[monthStr],
          uploaded: false,
          fileName: null,
          uploadDate: null
        });
        
        current.setMonth(current.getMonth() + 1);
      }
    });
    
    return required;
  };

  // Handle "Carregar SPEDs" button click
  const handleLoadSpeds = () => {
    // Validate selections
    if (selectedCompanies.length === 0) {
      alert('Selecione pelo menos uma empresa');
      return;
    }
    
    if (!startDate || !endDate) {
      alert('Selecione o período');
      return;
    }
    
    if (!isValidDate(startDate) || !isValidDate(endDate)) {
      alert('Período inválido');
      return;
    }
    
    // Calculate required SPEDs
    const companies = availableCompanies.filter(c => 
      selectedCompanies.includes(c.id)
    );
    const required = calculateRequiredSpeds(startDate, endDate, companies);
    
    setRequiredSpeds(required);
    setUploadedCount(0);
    setCurrentStep('upload');

    // Check for existing uploads
    checkExistingUploads(required);
  };

  // Handle continuar from modal
  const handleContinuarNovoRelatorio = () => {
    // Validar seleções
    if (selectedCompanies.length === 0) {
      toast.error('Selecione pelo menos uma empresa');
      return;
    }
    
    if (!startDate || !endDate) {
      toast.error('Selecione o período');
      return;
    }
    
    if (!isValidDate(startDate) || !isValidDate(endDate)) {
      toast.error('Período inválido');
      return;
    }
    
    if (startDateError || endDateError) {
      toast.error('Corrija os erros no período');
      return;
    }
    
    // Fechar modal
    setShowNovoRelatorioModal(false);
    
    // Avançar para upload
    handleLoadSpeds();
  };

  // Check for existing uploads and pre-populate
  const checkExistingUploads = async (requiredList) => {
    try {
      const currentProject = getCurrentProject();
      if (!currentProject) {
        addLog('warning', 'Nenhum projeto selecionado');
        return;
      }
      
      const companyCnpjs = [...new Set(requiredList.map(s => s.companyCnpj))];
      
      const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
      const token = authData.token;

      const response = await fetch(
        `${API_URL}/revisor/uploads/${currentProject.id}?` +
        `startDate=${startDate}&endDate=${endDate}&` +
        `companyIds=${companyCnpjs.join(',')}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (data.success && data.data.length > 0) {
        // Match existing uploads with required SPEDs
        let uploadCount = 0;
        
        setRequiredSpeds(prev => prev.map(sped => {
          const existing = data.data.find(upload => 
            upload.companyCnpj === sped.companyCnpj &&
            upload.year === sped.year &&
            upload.month === sped.month
          );

          if (existing) {
            uploadCount++;
            return {
              ...sped,
              uploaded: true,
              fileName: existing.fileName,
              uploadedAt: existing.uploadedAt
            };
          }

          return sped;
        }));

        setUploadedCount(uploadCount);
        
        if (uploadCount > 0) {
          addLog('info', `${uploadCount} arquivo(s) já enviado(s) anteriormente`);
        }
      }

    } catch (error) {
      console.error('Error checking existing uploads:', error);
      addLog('warning', 'Não foi possível verificar uploads anteriores');
    }
  };

  // Navigate back to config
  const goBackToConfig = () => {
    setCurrentStep('config');
  };

  // Handle file upload for a specific SPED
  // Logger Helper
  const addLog = (type, message) => {
    const timestamp = new Date().toLocaleTimeString();
    setUploadLogs(prev => [{ type, message, timestamp }, ...prev]);
    if (type === 'error') setUploadTab('logs');
  };

  // SPED Validator
  const validateSpedFile = (file, requiredSped) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const firstLine = content.split('\n')[0];
          const fields = firstLine.split('|');

          // Basic Structure Check
          if (fields.length < 8 || fields[1] !== '0000') {
            resolve({ valid: false, message: `Arquivo inválido (${file.name}): Registro 0000 não encontrado.` });
            return;
          }

          const fileDtIni = fields[4]; // DDMMAAAA
          const fileCnpj = fields[7];

          // Validate CNPJ
          const requiredCnpj = requiredSped.companyCnpj.replace(/\D/g, '');
          if (fileCnpj !== requiredCnpj) {
            resolve({ 
              valid: false, 
              message: `CNPJ incorreto em ${file.name}. Encontrado: ${fileCnpj}, Esperado: ${requiredCnpj} (${requiredSped.companyName})` 
            });
            return;
          }

          // Validate Period
          const fileMonth = parseInt(fileDtIni.substring(2, 4));
          const fileYear = parseInt(fileDtIni.substring(4, 8));

          if (fileMonth !== requiredSped.month || fileYear !== requiredSped.year) {
             resolve({ 
               valid: false, 
               message: `Período incorreto em ${file.name}. Encontrado: ${fileMonth}/${fileYear}, Esperado: ${requiredSped.month}/${requiredSped.year}` 
             });
             return;
          }

          resolve({ valid: true });
        } catch (err) {
          resolve({ valid: false, message: `Erro ao processar ${file.name}: ${err.message}` });
        }
      };
      reader.onerror = () => resolve({ valid: false, message: `Erro de leitura do arquivo ${file.name}.` });
      reader.readAsText(file.slice(0, 1024));
    });
  };



  // Internal function to handle file upload
  const internalUploadSpedFile = async (sped, file) => {
      // Validation
      const validation = await validateSpedFile(file, sped);
      if (!validation.valid) {
        addLog('error', validation.message);
        toast.error('Erro na validação do arquivo. Verifique a aba de Logs.');
        return false;
      }

      addLog('success', `SPED validado: ${file.name} (${sped.companyName})`);

      // Upload to backend
      try {
        const currentProject = getCurrentProject();
        if (!currentProject) {
          throw new Error('Nenhum projeto selecionado. Por favor, selecione um projeto.');
        }
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('projectId', currentProject.id);
        formData.append('companyId', sped.companyId);
        formData.append('companyCnpj', sped.companyCnpj);
        formData.append('companyName', sped.companyName);
        formData.append('year', sped.year);
        formData.append('month', sped.month);
        if (sped.startDate) formData.append('startDate', sped.startDate);
        if (sped.endDate) formData.append('endDate', sped.endDate);

        const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
        const token = authData.token;
        
        const response = await fetch(`${API_URL}/revisor/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Erro ao enviar arquivo');
        }

        // Update state on success
        setRequiredSpeds(prev => prev.map(s => {
          if (s.id === sped.id) {
            return { ...s, uploaded: true, fileName: file.name, uploadedAt: data.data.uploadedAt };
          }
          return s;
        }));
        
        setUploadedCount(prev => prev + 1);
        addLog('success', `SPED enviado ao servidor: ${file.name}`);
        toast.success(`SPED de ${sped.companyName} enviado com sucesso!`);
        return true;

      } catch (error) {
        console.error('Upload error:', error);
        addLog('error', `Erro ao enviar ${file.name}: ${error.message}`);
        toast.error(`Erro ao enviar SPED: ${error.message}`);
        return false;
      }
  };

  // Handle file upload for a specific SPED (Button Click)
  const handleUploadSped = async (sped) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      await internalUploadSpedFile(sped, file);
    };
    
    input.click();
  };

  // Process SPEDs (when all uploaded)
  const handleProcessSpeds = async () => {
    // Validation (commented out for testing, re-enable later)
    // if (uploadedCount < requiredSpeds.length) {
    //   toast.error(`Você precisa enviar todos os ${requiredSpeds.length} arquivos obrigatórios.`);
    //   return;
    // }

    setCurrentStep('processing');
    setProcessing(true);
    setUploadLogs([]);
    setUploadTab('logs');

    // Initialize tasks
    const initialTasks = [
        { id: 1, name: "Inicializando worker...", progress: 0, status: 'pending' },
        { id: 2, name: "Validando estrutura dos arquivos SPED...", progress: 0, status: 'pending' },
        { id: 3, name: "Importando Registro 0000 e 0150...", progress: 0, status: 'pending' },
        { id: 4, name: "Processando Blocos C (NFe/NFCe)...", progress: 0, status: 'pending' },
        { id: 5, name: "Processando Blocos D (CTe)...", progress: 0, status: 'pending' },
        { id: 6, name: "Cruzando dados com XMLs...", progress: 0, status: 'pending' },
        { id: 7, name: "Calculando conformidade...", progress: 0, status: 'pending' },
        { id: 8, name: "Gerando indexação Elasticsearch...", progress: 0, status: 'pending' },
        { id: 9, name: "Gerando ZIP do relatório...", progress: 0, status: 'pending' }
    ];

    setProcessingTasks(initialTasks);

    try {
        // Trigger backend processing
        const currentProject = getCurrentProject();
        if (!currentProject) {
          throw new Error('Nenhum projeto selecionado');
        }
        
        const companyCnpjs = selectedCompanies.map(id => {
            const company = availableCompanies.find(c => c.id === id);
            return company?.cnpj;
        }).filter(Boolean);

        // Notify websocketTaskService about new task (optimistic)
        const websocketTaskService = require('../../services/websocketTaskService').default;
        
        // Ensure we are subscribed to this project's channel
        websocketTaskService.updateProjectSubscription(currentProject.id);
        
        // Register optimistic task
        /*
        // Calling API below will trigger real updates.
        // We can just rely on the subscription to pick up the task when the worker starts.
        // But to be responsive, we can start with initial tasks.
        */

        const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
        const token = authData.token;
        
        const response = await fetch(`${API_URL}/revisor/process`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                projectId: currentProject.id,
                companyIds: companyCnpjs,
                startDate,
                endDate
            })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
            throw new Error(data.message || 'Erro ao iniciar processamento');
        }

        addLog('success', `Processamento iniciado (Job ID: ${data.jobId})`);

    } catch (error) {
        console.error('Processing error:', error);
        setProcessing(false);
        toast.error(`Erro ao processar SPEDs: ${error.message}`);
        addLog('error', `Erro: ${error.message}`);
    }
  };

  const toggleCompany = (companyId) => {
    setSelectedCompanies(prev => 
      prev.includes(companyId)
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  // Formatar data automaticamente (DD/MM/AAAA)
  const formatDate = (value) => {
    const numbers = value.replace(/\D/g, '');
    const limited = numbers.slice(0, 8);
    
    if (limited.length <= 2) {
      return limited;
    } else if (limited.length <= 4) {
      return `${limited.slice(0, 2)}/${limited.slice(2)}`;
    } else {
      return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
    }
  };

  // Validar se a data é válida
  const isValidDate = (dateString) => {
    if (dateString.length !== 10) return false;
    
    const [day, month, year] = dateString.split('/').map(Number);
    
    if (!day || !month || !year) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < 1900 || year > 2100) return false;
    
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day > daysInMonth) return false;
    
    return true;
  };

  // Converter string de data para objeto Date
  const parseDate = (dateString) => {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  };

  // Validar data inicial
  const validateStartDate = (date) => {
    if (!date) {
      setStartDateError('Data inicial é obrigatória');
      return false;
    }
    
    if (date.length === 10 && !isValidDate(date)) {
      setStartDateError('Data inválida');
      return false;
    }
    
    if (date.length === 10 && endDate.length === 10 && isValidDate(date) && isValidDate(endDate)) {
      const start = parseDate(date);
      const end = parseDate(endDate);
      
      if (start > end) {
        setStartDateError('Data inicial não pode ser maior que a final');
        return false;
      }
    }
    
    setStartDateError('');
    return true;
  };

  // Validar data final
  const validateEndDate = (date) => {
    if (!date) {
      setEndDateError('Data final é obrigatória');
      return false;
    }
    
    if (date.length === 10 && !isValidDate(date)) {
      setEndDateError('Data inválida');
      return false;
    }
    
    // Validar que não ultrapassa hoje
    if (date.length === 10 && isValidDate(date)) {
      const end = parseDate(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (end > today) {
        setEndDateError('Data final não pode ser maior que hoje');
        return false;
      }
    }
    
    if (date.length === 10 && startDate.length === 10 && isValidDate(date) && isValidDate(startDate)) {
      const start = parseDate(startDate);
      const end = parseDate(date);
      
      if (end < start) {
        setEndDateError('Data final não pode ser menor que a inicial');
        return false;
      }
    }
    
    setEndDateError('');
    return true;
  };

  const handleStartDateChange = (e) => {
    const formatted = formatDate(e.target.value);
    setStartDate(formatted);
    
    if (formatted.length === 10) {
      validateStartDate(formatted);
    } else {
      setStartDateError('');
    }
  };

  const handleEndDateChange = (e) => {
    const formatted = formatDate(e.target.value);
    setEndDate(formatted);
    
    if (formatted.length === 10) {
      validateEndDate(formatted);
    } else {
      setEndDateError('');
    }
  };

  // Selecionar todo o texto ao clicar
  const handleDateClick = (e) => {
    e.target.select();
  };

  // Revalidar quando uma data muda
  useEffect(() => {
    if (startDate.length === 10) {
      validateStartDate(startDate);
    }
  }, [endDate]);

  useEffect(() => {
    if (endDate.length === 10) {
      validateEndDate(endDate);
    }
  }, [startDate]);

  // Função para formatar período como "MÊS ANO"
  const getPeriodSummary = () => {
    if (startDate.length !== 10 || endDate.length !== 10) return '';
    if (!isValidDate(startDate) || !isValidDate(endDate)) return '';

    const start = parseDate(startDate);
    const end = parseDate(endDate);

    const months = ['JANEIRO', 'FEVEREIRO', 'MARÇO', 'ABRIL', 'MAIO', 'JUNHO',
                    'JULHO', 'AGOSTO', 'SETEMBRO', 'OUTUBRO', 'NOVEMBRO', 'DEZEMBRO'];

    // Se mesmo mês e ano
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${months[start.getMonth()]} ${start.getFullYear()}`;
    }

    // Se mesmo ano mas meses diferentes
    if (start.getFullYear() === end.getFullYear()) {
      return `${months[start.getMonth()]} - ${months[end.getMonth()]} ${start.getFullYear()}`;
    }

    // Anos diferentes
    return `${months[start.getMonth()]} ${start.getFullYear()} - ${months[end.getMonth()]} ${end.getFullYear()}`;
  };

  // Navegar para mês anterior
  const goToPreviousMonth = () => {
    if (startDate.length !== 10 || !isValidDate(startDate)) return;

    const current = parseDate(startDate);
    const previousMonth = new Date(current.getFullYear(), current.getMonth() - 1, 1);
    
    setStartDate(getFirstDayOfMonth(previousMonth));
    
    // Ajustar data final para último dia do mês anterior ou hoje (o que for menor)
    const lastDayOfPrevMonth = new Date(previousMonth.getFullYear(), previousMonth.getMonth() + 1, 0);
    const today = new Date();
    const endDateToSet = lastDayOfPrevMonth > today ? today : lastDayOfPrevMonth;
    
    const day = String(endDateToSet.getDate()).padStart(2, '0');
    const month = String(endDateToSet.getMonth() + 1).padStart(2, '0');
    setEndDate(`${day}/${month}/${endDateToSet.getFullYear()}`);
  };

  // Navegar para próximo mês
  const goToNextMonth = () => {
    if (startDate.length !== 10 || !isValidDate(startDate)) return;

    const current = parseDate(startDate);
    const nextMonth = new Date(current.getFullYear(), current.getMonth() + 1, 1);
    const today = new Date();

    // Não permitir avançar além do mês atual
    if (nextMonth.getMonth() > today.getMonth() || nextMonth.getFullYear() > today.getFullYear()) {
      if (nextMonth.getFullYear() > today.getFullYear()) return;
      if (nextMonth.getFullYear() === today.getFullYear() && nextMonth.getMonth() > today.getMonth()) return;
    }

    setStartDate(getFirstDayOfMonth(nextMonth));
    
    // Ajustar data final para último dia do próximo mês ou hoje (o que for menor)
    const lastDayOfNextMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0);
    const endDateToSet = lastDayOfNextMonth > today ? today : lastDayOfNextMonth;
    
    const day = String(endDateToSet.getDate()).padStart(2, '0');
    const month = String(endDateToSet.getMonth() + 1).padStart(2, '0');
    setEndDate(`${day}/${month}/${endDateToSet.getFullYear()}`);
  };

  // Verificar se pode avançar para próximo mês
  const canGoToNextMonth = () => {
    if (startDate.length !== 10 || !isValidDate(startDate)) return false;

    const current = parseDate(startDate);
    const nextMonth = new Date(current.getFullYear(), current.getMonth() + 1, 1);
    const today = new Date();

    // Permitir avançar se o próximo mês não ultrapassar o mês atual
    if (nextMonth.getFullYear() > today.getFullYear()) return false;
    if (nextMonth.getFullYear() === today.getFullYear() && nextMonth.getMonth() > today.getMonth()) return false;
    
    return true;
  };

  const formatDateDisplay = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPeriodTitle = (periodString) => {
    // Extrai o mês e ano do período (ex: "01/01/2024 - 31/12/2024" -> "JANEIRO 2024")
    if (!periodString) return 'PERÍODO NÃO DEFINIDO';
    
    const parts = periodString.split(' - ');
    if (parts.length !== 2) return periodString.toUpperCase();
    
    const [startDate] = parts;
    const [, startMonth, startYear] = startDate.split('/');
    
    const months = [
      'JANEIRO', 'FEVEREIRO', 'MARÇO', 'ABRIL', 'MAIO', 'JUNHO',
      'JULHO', 'AGOSTO', 'SETEMBRO', 'OUTUBRO', 'NOVEMBRO', 'DEZEMBRO'
    ];
    
    return `${months[parseInt(startMonth) - 1]} ${startYear}`;
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('pt-BR');
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return 'R$ 0,00';
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  // Se um fornecedor foi selecionado, mostra a tela de detalhes do fornecedor
  if (selectedReport && selectedSupplier) {
    return (
      <FornecedorDashboard
        key={selectedSupplier.code}
        supplier={selectedSupplier}
        report={selectedReport}
        reports={reports}
        onBack={() => setSelectedSupplier(null)}
        onOpenSupplier={(next) => setSelectedSupplier(next)}
      />
    );
  }

  // Se um relatório foi selecionado, mostra a tela de detalhes
  if (selectedReport) {
    return (
      <AnaliseDashboard
        key={selectedReport.id || selectedReport._id}
        report={selectedReport}
        reports={reports}
        onBack={() => {
          setSelectedReport(null);
          setSelectedSupplier(null);
          fetchReports();
          fetchKPIs();
        }}
        onOpenSupplier={(supplier) => setSelectedSupplier(supplier)}
        onReprocessStart={() => { knownAtStart.current = new Set(reports.map((r) => r.id)); }}
        onReprocessFinished={(reportId) => { setPendingOpenId({ id: reportId || null, known: knownAtStart.current }); fetchReports(); fetchKPIs(); }}
      />
    );
  }

  // Mês escolhido no mapa de cobertura: a criação abre esperando aquele mês
  const handleCreateMonth = (monthKey) => {
    setTargetMonth(monthKey);
    setShowNovoRelatorioModal(true);
  };
  const handleCreate = () => {
    setTargetMonth(null);
    setShowNovoRelatorioModal(true);
  };

  return (
    <>
      <RevisorHome
        reports={reports}
        loading={loading}
        processing={processing}
        processingTasks={processingTasks}
        updatedAt={updatedAt}
        onRefresh={() => { fetchReports(); fetchKPIs(); }}
        onCreate={handleCreate}
        onCreateMonth={handleCreateMonth}
        onOpenReport={(report) => setSelectedReport(reports.find((r) => r.id === report.id) || report)}
        deletingId={deletingId}
        deleteConfirm={deleteConfirm}
        onAskDelete={(report) => handleDeleteReport(report.id, periodLabel(normalizeReport(report)))}
        onCancelDelete={() => setDeleteConfirm(null)}
        onConfirmDelete={confirmDeleteReport}
      />

      <NovaAnaliseSheet
        isOpen={showNovoRelatorioModal}
        onClose={() => setShowNovoRelatorioModal(false)}
        targetMonth={targetMonth}
        analyzedMonths={analyzedMonths}
        onFinished={(reportId) => { setPendingOpenId({ id: reportId || null, known: knownAtStart.current }); fetchReports(); fetchKPIs(); }}
        onStart={() => { knownAtStart.current = new Set(reports.map((r) => r.id)); }}
      />
    </>
  );
}
